/**
 * @module EquipmentListManager
 * @description Component for displaying and managing equipment lists in the Conference Room Designer.
 */
import React from 'react';
import { Box, Typography, useTheme, Tabs, Tab } from '@mui/material';
import { useSelector, useDispatch } from "react-redux";
import Table from "components/molecules/Table";
import WalkthroughTitle from "components/atoms/WalkthroughTitle";
import { getErrorState, uiReducers } from "reduxModules/ducks/ui";
// Import the equipment list selector from container
import { getEquipmentList } from 'reduxModules/selectors/containerSelectors';
import { keys, isEmpty, values } from 'lodash';
import { EquipmentTableManagerProps, EquipmentListState } from 'types/services/EquipmentTableManager';

/**
 * @function EquipmentListManager
 * @description Renders a table of equipment with title and optional notes.
 * Shows error message if design has validation errors.
 * 
 * @component
 * @returns {React.Element} Equipment list display component
 * 
 * @description
 * This component:
 * 1. Displays equipment list data in a tabular format
 * 2. Shows error message if design validation errors exist
 * 3. Renders optional notes below the table
 * 4. Handles empty data states
 * 
 * States handled:
 * - Error state: Shows error message instead of table
 * - Empty data: Renders nothing except title
 * - Normal state: Renders title, table, and optional notes
 * 
 * @example
 * // Example equipment list structure
 * {
 *   title: "Room Equipment List",
 *   tableData: [
 *     { item: "Speaker", quantity: 2, model: "SP100" },
 *     { item: "Microphone", quantity: 1, model: "MC200" }
 *   ],
 *   widths: [40, 20, 40],
 *   notes: ["Note 1", "Note 2"]
 * }
 */
const EquipmentListManager: React.FC<EquipmentTableManagerProps> = () => {
  const dispatch = useDispatch();
  const error = useSelector(getErrorState);
  // Use useMemo to memoize the error messages to prevent unnecessary rerenders  
  const errors = useSelector((state: any) => state.ui.error || {});
  const errorMessages = React.useMemo(() => {
    return Object.values(errors)
      .filter((err: any) => err && err.msg)
      .map((err: any) => err.msg);
  }, [errors]);
  const errorTitle = errorMessages.length > 0 
    ? `Error: ${errorMessages.join(', ')}`
    : "can't display equipment list until design errors are cleared";
  const equipmentList = useSelector(getEquipmentList) as EquipmentListState;
  const theme = useTheme();
  
  // Get current tab value from Redux state, default to summary
  const currentTab = useSelector((state: any) => state.ui.showTable) || 'summary';
  const currentView = useSelector((state: any) => state.ui.currentView);
  
  // Reset to summary tab when equipment list is first opened
  React.useEffect(() => {
    if (currentView === 'equipmentlist') {
      dispatch(uiReducers.setShowTable('summary'));
    }
  }, [currentView, dispatch]);
  
  // Get active tab to access the correct area
  const activeTab = useSelector((state: any) => state.container.activeTab);
  
  // Check if cameras are present in the current area
  const hasCamera = useSelector((state: any) => {
    const areas = state.container.areas;
    if (areas && areas[activeTab]) {
      const cameras = areas[activeTab].cameras;
      if (cameras) {
        // Camera tab should be visible if hasCamera is true AND model is not 'none'
        const hasCameraFlag = cameras.hasCamera !== undefined ? cameras.hasCamera : true;
        const hasValidCameraModel = cameras.model && cameras.model !== 'none';
        return hasCameraFlag && hasValidCameraModel;
      }
    }
    return false;
  });
  
  // Destructure with defaults to handle potentially undefined properties
  const { 
    title = '', 
    tableData = [], 
    widths = [], 
    notes = [] 
  } = equipmentList || {};

  const tableTitle = error ? errorTitle : title;
  const emptyData = isEmpty(tableData);
  // Build headers from the UNION of every row's keys (in first-seen order), not just row 0.
  // Positioning rows carry optional columns — e.g. a bar speaker has no Height (Z) but a pendant
  // expansion speaker does; keying off row 0 alone dropped the header for columns only later rows
  // had, so those values rendered in an unlabeled overflow column (PIG-585).
  const headers = emptyData
    ? []
    : Array.from(
        tableData.reduce((set, row) => {
          if (row) keys(row).forEach((k) => set.add(k));
          return set;
        }, new Set<string>())
      );
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    dispatch(uiReducers.setShowTable(newValue));
  };

  return (
    <Box
      id="equipmentlist"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        marginTop: '40px',
        marginX: 'auto',
        width: '90%',
        backgroundColor: theme.palette.background.default,
        padding: '24px',
        borderRadius: '8px',
        overflow: 'auto', // Allow scrolling if content is too long
      }}
    >
      {!error && !emptyData && (
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            marginBottom: '24px',
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTabs-indicator': {
              backgroundColor: '#007bff',
              height: 3,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '15px',
              fontWeight: 400,
              color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              '&:hover': {
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
                backgroundColor: theme.palette.action.hover,
              },
              '&.Mui-selected': {
                color: '#007bff',
                fontWeight: 500,
              },
            },
          }}
        >
          <Tab label="Summary" value="summary" />
          <Tab label="Microphone locations" value="microphonePositioning" />
          <Tab label="Speaker locations" value="speakerPositioning" />
          {hasCamera && <Tab label="Camera locations" value="cameraPositioning" />}
        </Tabs>
      )}
      
      {error && (
        <Box
          sx={{
            textTransform: 'uppercase',
            marginBottom: '16px',
            textAlign: 'center',
            fontSize: '22px',
            color: theme.palette.error.main,
          }}
        >
          <WalkthroughTitle>
            {errorTitle}
          </WalkthroughTitle>
        </Box>
      )}
      
      {!error && !emptyData && (
        <Table 
          headers={headers} 
          rows={tableData.map(item => {
            if (!item) return headers.map(() => '');

            // Map over the shared header set (not the row's own keys) so every cell lands under
            // its column and rows missing an optional column render a blank cell there.
            return headers.map(header => {
              const cellValue = (item as Record<string, any>)[header];
              // Handle null/undefined values safely
              if (cellValue === null || cellValue === undefined) {
                return '';
              }
              
              // Handle different types of cell values
              try {
                // If it's an object, check for name/link pattern
                if (cellValue !== null && typeof cellValue === 'object') {
                  // TypeScript safe way to check for properties
                  const obj = cellValue as Record<string, any>;
                  if (obj.name !== undefined && obj.link !== undefined) {
                    return String(obj.name);
                  }
                }
                
                // Convert to string for all other cases
                return String(cellValue);
              } catch (error) {
                // Fallback for any unexpected errors
                console.error("Error processing cell value:", error);
                return '';
              }
            });
          })} 
        />
      )}
      
      {!error && !emptyData && notes.length > 0 && (
        <Box
          sx={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)' 
              : 'rgba(0, 0, 0, 0.02)',
            borderRadius: '4px',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {notes.map((note, index) => (
            <Box
              key={`note-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: index < notes.length - 1 ? '8px' : 0,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Open Sans',
                  color: theme.palette.text.secondary,
                  marginRight: '8px',
                }}
              >
                •
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Open Sans',
                  color: theme.palette.text.secondary,
                  lineHeight: 1.5,
                }}
              >
                {note}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default EquipmentListManager;
